/**
 * The AMOUNT semantics of the stored transform vocabulary — the transform
 * counterpart of `row-condition-semantics.ts`.
 *
 * `bat.exchange_api_templates.pattern_rules` field mappings carry a list of
 * transforms, and THREE engines apply them:
 *   - `PatternMatchingService` — the engine that classifies a real sync;
 *   - `ApiPatternMatcherService` — the super-admin server preview ("Simular");
 *   - `clientSideTransformer` — the in-browser "Produces transaction" panel the
 *     admin watches while authoring a rule.
 *
 * #1076 unified the two BACKEND engines here after their private copies
 * disagreed on every arithmetic transform: the preview used `Number(value)`
 * while the import used an exact Decimal over a non-numeric-stripped string, so
 * `abs` on the stored value '1,5' previewed as the string 'NaN' and imported as
 * 15. The BROWSER engine was left on its own float copy carrying the same
 * drifts, and after #1082 made it match the rows the import matches, the panel
 * started printing `"incomingAmount": "NaN"` on screen where the server
 * produced '1.23456' for the SAME row and the SAME shipped Kraken rule.
 *
 * The module therefore lives in `@bat/shared` rather than in the backend: the
 * browser is one of the three consumers and must not fork it again. The
 * semantics are the IMPORT engine's, because that engine classifies real user
 * data and also serves user-authored CSV mappings in `bat.import_mappings` —
 * same direction #1073/#1074 took. Exact Decimal, never float: crypto amounts
 * carry more than 15 significant digits.
 */

import type { Decimal } from 'decimal.js'

import {
  formatDecimalAmountTrimmed,
  parseDecimalWithParseFloatSemantics,
} from '../../utils/financial-decimal.js'

/**
 * A transform that CANNOT run — an unimplemented type, a missing/unusable
 * parameter, or an operand that is not a number.
 *
 * It is an error and not a silent fallback on purpose. Both engines used to
 * answer a non-runnable transform with a value: the import wrote the number
 * `NaN` into an amount, the preview wrote the string `'NaN'`, and an
 * unimplemented type silently passed the untransformed value straight through.
 * A number that is wrong is unrecoverable; a field that is missing and reported
 * is recoverable, so this throws and the callers disclose it.
 */
export class FieldTransformError extends Error {
  constructor(
    readonly transformType: string,
    readonly reason: string,
  ) {
    super(`Transform '${transformType}' cannot run: ${reason}`)
    this.name = 'FieldTransformError'
  }
}

/**
 * Parse an amount operand exactly — the import engine's own cleaning step,
 * character for character. Non-numeric characters are stripped first (so
 * '12 BTC' reads as 12), then `parseFloat` semantics apply, but the digits land
 * in a Decimal instead of a float.
 *
 * The strip list deliberately excludes `e`, which means '1e-9' reads as 1 and
 * not as a billionth. That is the behaviour of every amount this engine has
 * ever imported; widening it here would silently re-scale live data, which is
 * not what a parity change may do.
 */
const parseAmountOperand = (value: unknown): Decimal | null =>
  parseDecimalWithParseFloatSemantics(String(value).replace(/[^0-9.-]/g, ''))

/**
 * Whether an operand would survive {@link parseAmountOperand} — i.e. whether an
 * arithmetic transform over it can produce a number at all.
 *
 * Exported for callers that must DISCLOSE an unusable operand before the
 * transform runs (the browser preview paints a warning chip on the offending
 * mapping). It exists so that "is this operand usable?" has ONE definition
 * shared with the arithmetic, instead of a caller re-deriving it with
 * `Number.isFinite` and drifting the moment the strip list changes.
 */
export function isUsableTransformAmountOperand(value: unknown): boolean {
  return parseAmountOperand(value) !== null
}

/** Parse an operand or refuse to run — the shared failure mode of every case below. */
function requireAmountOperand(value: unknown, transformType: string): Decimal {
  const parsed = parseAmountOperand(value)
  if (parsed === null) {
    throw new FieldTransformError(
      transformType,
      `'${String(value)}' is not a number, so the result would not be one either`,
    )
  }
  return parsed
}

/** `abs` — magnitude of an amount, as an exact plain decimal string. */
export function applyAbsoluteTransform(value: unknown): string {
  return formatDecimalAmountTrimmed(requireAmountOperand(value, 'abs').abs())
}

/** `negate` — sign flip of an amount, as an exact plain decimal string. */
export function applyNegateTransform(value: unknown): string {
  return formatDecimalAmountTrimmed(requireAmountOperand(value, 'negate').negated())
}

/** `parseNumber` — the CSV DSL's own spelling of "read this cell as an amount". */
export function applyParseNumberTransform(value: unknown): string {
  return formatDecimalAmountTrimmed(requireAmountOperand(value, 'parseNumber'))
}

/**
 * `parseAmount` — read an amount written in the source's own decimal
 * convention. `decimalSeparator: ','` means '1.234,56' is one thousand two
 * hundred thirty four and fifty six hundredths, so the dots are thousands marks
 * and the comma is the decimal point; with '.' the commas are the thousands
 * marks. The editor ships a dropdown for exactly this choice.
 *
 * The previous preview-only implementation replaced the FIRST comma with a dot
 * and left the thousands marks alone, so '1.234,56' became '1.234.56'. No
 * stored rule used the transform, so this defines it rather than changing it.
 */
export function applyParseAmountTransform(value: unknown, decimalSeparator: string): string {
  const raw = String(value)
  const normalized =
    decimalSeparator === ','
      ? raw.replace(/\./g, '').replace(',', '.')
      : // '.' (the default): commas can only be thousands marks.
        raw.replace(/,/g, '')

  return formatDecimalAmountTrimmed(requireAmountOperand(normalized, 'parseAmount'))
}

/**
 * `multiply` / `divide` — scale an amount by a CONSTANT stored on the rule
 * (`params.factor`). Used to convert a source that quotes in a different unit
 * (satoshis → BTC is `divide` by 1e8).
 *
 * A missing factor is refused rather than defaulted to 1: a rule that silently
 * became the identity would look like it worked. The admin schema refuses the
 * same shapes at authoring time, so a stored rule cannot reach this.
 */
export function applyFactorTransform(
  value: unknown,
  factor: unknown,
  operation: 'multiply' | 'divide',
): string {
  const parsedFactor = parseAmountOperand(factor)
  if (parsedFactor === null) {
    throw new FieldTransformError(operation, `params.factor is missing or not a number`)
  }
  if (operation === 'divide' && parsedFactor.isZero()) {
    throw new FieldTransformError('divide', 'params.factor is zero')
  }

  const operand = requireAmountOperand(value, operation)
  const result = operation === 'multiply' ? operand.times(parsedFactor) : operand.div(parsedFactor)
  return formatDecimalAmountTrimmed(result)
}

/**
 * `multiplyByField` — scale an amount by a SIBLING field of the same record
 * (Coinbase/Bitvavo compute the quote leg as `size × price`).
 *
 * Deliberately KEEPS the import engine's established fallback — an absent or
 * unparseable factor leaves the value untouched instead of throwing — because
 * four seeded rules use it and this change is not the place to move them.
 * `factorRaw === undefined` means the record has no such field.
 *
 * A caller that needs to TELL the user the value came back unscaled asks
 * {@link isUsableTransformAmountOperand} about both operands first, rather than
 * inferring it from the returned value (an operand that is already canonical
 * comes back byte-identical from a successful multiply by 1).
 */
export function applyMultiplyByFieldTransform(value: unknown, factorRaw: unknown): unknown {
  if (factorRaw === null || factorRaw === undefined) {
    return value
  }
  const multiplicand = parseAmountOperand(value)
  const factor = parseAmountOperand(factorRaw)
  if (multiplicand === null || factor === null) {
    return value
  }
  return formatDecimalAmountTrimmed(multiplicand.times(factor))
}
